import { template as template_ff6a5eac14634b279d6826ac7446abae } from "@ember/template-compiler";
import Component from '@glimmer/component';
import { service } from '@ember/service';
import EdhrMethodTypes from 'eflex/constants/tasks/edhr-method-types';
import EdhrProcessDataTypes from 'eflex/constants/edhr-process-data-types';
import { taskTypes } from 'eflex/constants/tasks/task-types';
import { concatRight, sortByProps, sortByProp } from 'ramda-adjunct';
import { range, pipe } from 'ramda';
import HardwareSelect from 'eflex/components/hardware-select';
import TrashCan from 'eflex/components/trash-can';
import QuantitySelect from 'eflex/components/quantity-select';
import { t } from 'ember-intl';
import { fn } from '@ember/helper';
import { notEq, eq } from 'ember-truth-helpers';
import { append } from '@eflexsystems/ember-composable-helpers';
const HARDWARE_TYPES = [
    'edhr'
];
const METHODS_WITH_REASON_CODES = new Set([
    EdhrMethodTypes.sendData,
    EdhrMethodTypes.completeOperation
]);
const IMAGE_TASK_TYPES = new Set([
    taskTypes.imageCapture,
    taskTypes.vision
]);
const edhrMethodTypeValues = Object.values(EdhrMethodTypes);
export default class EdhrDeviceConfig extends Component {
    @service
    store;
    @service
    intl;
    get previousTasks() {
        const allTasks = sortByProps([
            'row',
            'column'
        ], this.args.task.parent?.children ?? []);
        const index = allTasks.indexOf(this.args.task);
        if (index === -1) {
            return [];
        }
        return allTasks.slice(0, index);
    }
    get showReasonCode() {
        return METHODS_WITH_REASON_CODES.has(this.args.task.edhrMethod);
    }
    get processDataOptions() {
        return pipe(concatRight(this.variableDefs ?? []), concatRight(this.images ?? []), concatRight(this.barcodes ?? []), concatRight(this.decisions ?? []), sortByProp('groupName'), concatRight([
            this._getMappingOption(EdhrProcessDataTypes.currentDateTime),
            this._getMappingOption(EdhrProcessDataTypes.userDefinedString)
        ]))(this.bolts ?? []);
    }
    get decimalOptions() {
        const keys = Array.from({
            length: 11
        }).keys();
        return [
            ...keys
        ].map(String);
    }
    get bolts() {
        const torqueTasks = this.previousTasks.filter((item)=>item.taskType === taskTypes.torque);
        return torqueTasks.map((task)=>{
            const options = range(1, task.maxBoltCount + 1).flatMap((i)=>{
                return [
                    this._boltToProcessDatum(i, EdhrProcessDataTypes.boltTorque, task),
                    this._boltToProcessDatum(i, EdhrProcessDataTypes.boltAngle, task)
                ];
            });
            options.unshift(this._getMappingOption(EdhrProcessDataTypes.hardwareName, task));
            return {
                groupName: task.name,
                options
            };
        });
    }
    get variableDefs() {
        return this.previousTasks.filter((item)=>item.variableDefs?.length).map((task)=>({
                groupName: task.name,
                options: [
                    this._getMappingOption(EdhrProcessDataTypes.hardwareName, task),
                    ...task.variableDefs.filter((item)=>item.isFromDevice).map((variableDef)=>({
                            id: variableDef.id,
                            processDataType: EdhrProcessDataTypes.variableDef,
                            name: variableDef.name,
                            variableDef,
                            dataTask: task
                        }))
                ]
            }));
    }
    get images() {
        const imageTasks = this.previousTasks.filter((task)=>IMAGE_TASK_TYPES.has(task.taskType));
        return imageTasks.map((task)=>{
            return {
                groupName: task.name,
                options: [
                    this._getMappingOption(EdhrProcessDataTypes.hardwareName, task),
                    this._getMappingOption(EdhrProcessDataTypes.sendImage, task)
                ]
            };
        });
    }
    get barcodes() {
        const barcodeTasks = this.previousTasks.filter((item)=>item.taskType === taskTypes.barcode);
        return barcodeTasks.map((task)=>{
            return {
                groupName: task.name,
                options: [
                    this._getMappingOption(EdhrProcessDataTypes.hardwareName, task),
                    this._getMappingOption(EdhrProcessDataTypes.scannedBarcode, task)
                ]
            };
        });
    }
    get decisions() {
        const decisionTasks = this.previousTasks.filter((item)=>item.taskType === taskTypes.decision);
        return decisionTasks.map((task)=>{
            return {
                groupName: task.name,
                options: [
                    this._getMappingOption(EdhrProcessDataTypes.selectedDecision, task)
                ]
            };
        });
    }
    _getMappingOption(processDataType, dataTask) {
        return {
            id: processDataType,
            processDataType,
            name: this.intl.t(processDataType),
            dataTask
        };
    }
    _boltToProcessDatum(boltNumber, processDataType, task) {
        return {
            id: this._getBoltId(boltNumber, processDataType),
            processDataType,
            name: `${this.intl.t('bolt')} ${boltNumber} ${this.intl.t(processDataType)}`,
            boltIndex: boltNumber,
            dataTask: task
        };
    }
    _getBoltId(boltNumber, boltFieldType) {
        return String(boltNumber) + boltFieldType;
    }
    getDataMappingReference = (mapping)=>{
        const group = this.processDataOptions.find((option)=>{
            return option.options?.find((item)=>item.dataTask === mapping.dataTask);
        });
        switch(mapping.processDataType){
            case EdhrProcessDataTypes.variableDef:
                {
                    return group?.options.find((item)=>item.id === mapping.variableDef?.id);
                }
            case EdhrProcessDataTypes.boltTorque:
            case EdhrProcessDataTypes.boltAngle:
                {
                    return group?.options.find((item)=>item.id === this._getBoltId(mapping.boltIndex, mapping.processDataType));
                }
            default:
                {
                    if (group) {
                        return group.options.find((item)=>item.id === mapping.processDataType);
                    }
                    return this.processDataOptions.find((item)=>item.id === mapping.processDataType);
                }
        }
    };
    addMapping = (count)=>{
        for(let i = 1; i <= count; i++){
            this.store.createRecord('edhr-mapping', {
                task: this.args.task
            });
        }
    };
    removeMapping = (mapping)=>{
        mapping.deleteRecord();
    };
    setProcessDatum = (mapping, processDatum)=>{
        Object.assign(mapping, {
            boltIndex: processDatum.boltIndex ?? null,
            processDataType: processDatum.processDataType,
            variableDef: processDatum.variableDef,
            dataTask: processDatum.dataTask
        });
    };
    static{
        template_ff6a5eac14634b279d6826ac7446abae(`
    <div class="row form-row">
      <div class="col-6">
        <HardwareSelect
          @types={{HARDWARE_TYPES}}
          @model={{@task}}
          @form={{@form}}
        />
      </div>
    </div>

    <hr/>

    <div class="row form-row">
      <div class="col-6">
        <@form.element
          class="edhr-operation"
          @controlType="text"
          @label={{t "operation"}}
          @property="operation"
        />
        {{#if @task.usesModels}}
          <div class='col-auto'>
            <@form.element
              @controlType='checkbox'
              @property='operationUsesModel'
              @label={{t 'useModelCode'}}
              class="use-model-code"
            />
          </div>
        {{/if}}
      </div>

      <div class="col-6">
        <@form.element
          @controlType="power-select"
          class="method-type-select"
          @property="edhrMethod"
          @label={{t "methodType"}}
          @options={{edhrMethodTypeValues}}
          @onChange={{fn (mut @task.edhrMethod)}}
        as |el|>
          <el.control
            class="edhr-method-select"
            @triggerClass="form-control"
          as |option|>
            {{t option}}
          </el.control>
        </@form.element>
      </div>
    </div>

    {{#if this.showReasonCode}}
      <div class="row form-row">
        <div class="col-6">
          <@form.element
            @controlType="text"
            class="reason-code"
            @label={{t "reasonCode"}}
            @property="reasonCode"
          />
        </div>
      </div>
    {{/if}}

    {{#if (eq EdhrMethodTypes.sendData @task.edhrMethod)}}
      <div class="bootstrap-table mt-2">
        <div class="row form-row header-row">
          <div class="col">
            {{t "processData"}}
          </div>
        </div>

        {{#each @task.nonDeletedEdhrMappings as |mapping|}}
          <div class="mapping-row row form-row align-items-start">
            <div class="col-4">
              <div class="nested-header">
                {{t "type"}}
              </div>
              <@form.element
                @controlType="power-select"
                class="process-data-type-select"
                @model={{mapping}}
                @options={{this.processDataOptions}}
                @optionLabelPath="name"
                @value={{this.getDataMappingReference mapping}}
                @onChange={{fn this.setProcessDatum mapping}}
              as |el|>
                <el.control
                  @triggerClass="form-control form-control-sm"
                  @searchEnabled={{true}}
                  @searchField="name"
                />
              </@form.element>
              {{#if mapping.dataTask}}
                <div class="nested-header text-truncate">
                  <span class="data-task-name" title={{mapping.dataTask.name}}>
                    {{mapping.dataTask.name}}
                  </span>
                </div>
              {{/if}}
            </div>

            <div class="col">
              {{#if (notEq mapping.processDataType EdhrProcessDataTypes.sendImage)}}
                <div class="nested-header">
                  {{t "fieldName"}}
                </div>
                <@form.element
                  class="field-name"
                  @controlType="text"
                  @model={{mapping}}
                  @property="fieldName"
                  @size="sm"
                />
              {{/if}}
            </div>

            {{#if (eq mapping.processDataType EdhrProcessDataTypes.userDefinedString)}}
              <div class="col-4">
                <div class="nested-header">
                  {{t "userDefinedString"}}
                </div>
                <@form.element
                  @controlType="text"
                  class="user-string"
                  @model={{mapping}}
                  @property="customValue"
                />
              </div>
            {{/if}}

            {{#if mapping.canBeNumber}}
              <div class="col-2">
                <div class="nested-header">
                  {{t "dataType.decimal"}}
                </div>
                <@form.element
                  @controlType="power-select"
                  class="decimal-precision"
                  @options={{append null this.decimalOptions}}
                  @model={{mapping}}
                  @property="decimalPrecision"
                as |el|>
                  <el.control
                    @placeholder="--"
                    @allowClear={{true}}
                  />
                </@form.element>
              </div>
            {{/if}}

            <div class="col-auto">
              <div class='nested-header'></div>
              <div class="col-icon mt-3">
                <TrashCan
                  class='align-self-center'
                  disabled={{@disabled}}
                  @onDelete={{fn this.removeMapping mapping}}
                />
              </div>
            </div>
          </div>
        {{else}}
          <div class="row form-row">
            <div class="m-2 fst-italic">
              {{t 'noEdhrProcessData' }}
            </div>
          </div>
        {{/each}}
      </div>

      <div class="row form-row pt-0 add-process-data">
        <div class="col">
          <QuantitySelect
            class="add-mapping text-uppercase"
            @disabled={{@disabled}}
            @onSubmit={{this.addMapping}}
          >
            {{t "addProcessData"}}
          </QuantitySelect>
        </div>
      </div>
    {{/if}}
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
}
